"use client";
import { Track } from "livekit-client";
import { useTranslations } from "next-intl";
import { memo, useMemo, useState, useEffect } from "react";
import { VideoGridProps } from "@client/types/videoGrid.types";
import { ChevronUp, ChevronDown, Users, Mic, MicOff } from "lucide-react";
import { ParticipantTile } from "@client/components/meet/ParticipantTile";
import { ScreenShareWarning } from "./ScreenShareWarning";
import { resolveParticipantAvatarUrl } from "@client/utils/livekitParticipantMetadata";
import { getHandRaiseQueuePosition, sortCameraTracksByRaisedHand } from "@client/utils/handRaiseQueue";
import { useTracks, TrackRefContext, TrackReferenceOrPlaceholder } from "@livekit/components-react";

/**
 * Grid layout configuration.
 * Maps participant count to grid dimensions.
 * For 3 participants: Use 4-column grid with special item placement (2 top, 1 centered bottom)
 */
const GRID_CLASSES: Record<number, string> = {
  1: "grid-cols-1 lg:grid-rows-1",
  2: "grid-cols-1 sm:grid-cols-2 lg:grid-rows-1",
  3: "grid-cols-1 sm:grid-cols-4 lg:grid-rows-2",
  4: "grid-cols-2 auto-rows-fr lg:grid-rows-2",
  5: "grid-cols-2 auto-rows-fr sm:grid-cols-3 lg:grid-rows-2",
  6: "grid-cols-2 auto-rows-fr sm:grid-cols-3 lg:grid-rows-2",
  7: "grid-cols-2 auto-rows-fr sm:grid-cols-3 lg:grid-rows-2",
  8: "grid-cols-2 auto-rows-fr sm:grid-cols-4 lg:grid-rows-2",
  9: "grid-cols-2 auto-rows-fr sm:grid-cols-3 lg:grid-rows-3"
};

/**
 * Get special grid item classes for 3-participant layout.
 * Returns col-span classes for 2-on-top, 1-centered-below pattern.
 */
const getThreeParticipantClass = (index: number): string => {
  // On desktop (sm+): first 2 span 2 cols each, 3rd spans 2 cols centered
  // col-span-2 makes each take half of 4-col grid
  // 3rd item: col-start-2 centers it (starts at col 2 of 4)
  if (index === 2) {
    return "sm:col-span-2 sm:col-start-2"; // Centered bottom
  }
  return "sm:col-span-2"; // Top row items
};

const DEFAULT_GRID_CLASS = "grid-cols-2 sm:grid-cols-4 auto-rows-fr";

/**
 * Check if a participant is in the waiting room.
 * Returns true if permissions.canPublish is explicitly false.
 */
const isWaitingRoomParticipant = (participant: TrackReferenceOrPlaceholder["participant"]): boolean => {
  // If permissions are not yet loaded, assume they can publish (will be filtered when permissions arrive)
  if (!participant.permissions) return false;
  // Lobby tokens have canPublish=false - these are waiting room participants
  return participant.permissions.canPublish === false;
};

/** VideoGrid - Displays participants in a responsive grid */
export const VideoGrid = memo(function VideoGrid({
  localParticipantSid,
  participantReactions,
  isSidebarOpen: _,
  zoomLevel,
  zoomScrollerRef,
  onZoomMouseDown,
  onZoomMouseLeave,
  onZoomMouseUp,
  onZoomMouseMove,
  onToggleSettings,
  onToggleParticipants,
  canModerate = false,
  canRequestUnmute = false,
  onLowerParticipantHand,
  onMuteParticipant,
  onUnmuteParticipant,
  onMuteParticipantVideo,
  onUnmuteParticipantVideo,
  onKickParticipant,
  participantAvatarByIdentity,
  localAvatarUrl,
  isLocalScreenSharing = false,
  onStopScreenShare
}: VideoGridProps) {
  const t = useTranslations();

  const avatarUrlForParticipant = (track: TrackReferenceOrPlaceholder) => {
    const isLocal = track.participant.sid === localParticipantSid;
    return resolveParticipantAvatarUrl(track.participant, participantAvatarByIdentity, localAvatarUrl, isLocal);
  };

  const tileModerationProps = (isLocal: boolean) => {
    if (!canModerate || isLocal) return {};
    return {
      canModerate: true,
      canRequestUnmute,
      onMuteParticipant,
      onUnmuteParticipant,
      onMuteParticipantVideo,
      onUnmuteParticipantVideo,
      onKickParticipant
    };
  };
  // Track options - subscribe to all tracks including unsubscribed to get screen shares
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
      { source: Track.Source.ScreenShareAudio, withPlaceholder: false }
    ],
    { onlySubscribed: false }
  );

  const [pinnedTrackSid, setPinnedTrackSid] = useState<string | null>(null);
  const [mobileParticipantsExpanded, setMobileParticipantsExpanded] = useState(false);

  /* Responsive logic for participant limit */
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      // < 1024px is generally considered tablet/mobile in our grid design (lg breakpoint)
      setIsMobileOrTablet(window.innerWidth < 1024);
    };

    // Debounce resize handler to prevent excessive re-renders
    let timeoutId: ReturnType<typeof setTimeout>;
    const debouncedCheck = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkIsMobile, 150);
    };

    // Initial check
    checkIsMobile();

    // Listen for resize with debounce
    window.addEventListener("resize", debouncedCheck);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", debouncedCheck);
    };
  }, []);

  /**
  /**
   * categorize tracks into Screen Shares and Cameras.
   * Filters out waiting room participants who shouldn't appear yet.
   */
  const { screenShareTracks, cameraTracks, hasScreenShare, pinnedTrack } = useMemo(() => {
    const screenShare: TrackReferenceOrPlaceholder[] = [];
    const camera: TrackReferenceOrPlaceholder[] = [];
    let pinned: TrackReferenceOrPlaceholder | undefined;

    for (const t of tracks) {
      // Skip waiting room participants - they shouldn't appear in the grid until approved
      if (isWaitingRoomParticipant(t.participant)) {
        continue;
      }

      if (t.source === Track.Source.ScreenShare) {
        screenShare.push(t);
      } else if (t.source === Track.Source.Camera) {
        camera.push(t);
      }

      // Check if this is the pinned track
      if (pinnedTrackSid && (t.publication?.trackSid === pinnedTrackSid || t.participant.sid === pinnedTrackSid)) {
        pinned = t;
      }
    }

    return {
      screenShareTracks: screenShare,
      cameraTracks: camera,
      hasScreenShare: screenShare.length > 0,
      pinnedTrack: pinned
    };
  }, [tracks, pinnedTrackSid]);

  const handlePin = (sid: string) => {
    setPinnedTrackSid((prev) => (prev === sid ? null : sid));
  };

  const sortedCameraTracks = useMemo(
    () => sortCameraTracksByRaisedHand(cameraTracks, participantReactions),
    [cameraTracks, participantReactions]
  );

  const tileHandProps = (participantSid: string, isLocal: boolean) => ({
    isHandRaised: participantReactions?.get(participantSid)?.isHandRaised || false,
    handQueuePosition: getHandRaiseQueuePosition(participantSid, participantReactions),
    canModerateLowerHand: canModerate,
    onLowerParticipantHand: canModerate && !isLocal ? onLowerParticipantHand : undefined
  });

  // Screen Share or Pinned Layout
  if (hasScreenShare || pinnedTrack) {
    const focusTrack = hasScreenShare ? screenShareTracks[0] : pinnedTrack;

    // Filter out the focus track from the side list if it's there
    const sideTracks = sortedCameraTracks.filter((t) => t !== focusTrack);

    return (
      <div className="h-full flex flex-col lg:flex-row gap-2" role="main">
        {/* Main presentation/pinned video - takes full width on mobile, flex-1 on desktop */}
        <div className="flex-1 flex flex-col gap-2 min-w-0 animate-zoom-in overflow-hidden relative min-h-0">
          {focusTrack && (
            <>
              <div
                ref={zoomScrollerRef}
                data-focus-video="true"
                className={`w-full h-full rounded-xl transition-transform duration-200 ease-in-out scrollbar-hide ${
                  (zoomLevel ?? 1) > 1 ? "overflow-auto cursor-grab active:cursor-grabbing" : "overflow-hidden"
                }`}
                onMouseDown={onZoomMouseDown}
                onMouseLeave={onZoomMouseLeave}
                onMouseUp={onZoomMouseUp}
                onMouseMove={onZoomMouseMove}
              >
                <div
                  style={{
                    transform: `scale(${zoomLevel ?? 1})`,
                    transformOrigin: "top left",
                    width: (zoomLevel ?? 1) > 1 ? `${(zoomLevel ?? 1) * 100}%` : "100%",
                    height: (zoomLevel ?? 1) > 1 ? `${(zoomLevel ?? 1) * 100}%` : "100%"
                  }}
                  className="h-full w-full"
                >
                  <TrackRefContext.Provider
                    key={focusTrack.publication?.trackSid || focusTrack.participant.sid}
                    value={focusTrack}
                  >
                    <ParticipantTile
                      track={focusTrack}
                      isLocal={focusTrack.participant.sid === localParticipantSid}
                      avatarUrl={avatarUrlForParticipant(focusTrack)}
                      {...tileHandProps(focusTrack.participant.sid, focusTrack.participant.sid === localParticipantSid)}
                      {...tileModerationProps(focusTrack.participant.sid === localParticipantSid)}
                      onPin={() => handlePin(focusTrack.participant.sid)}
                      isPinned={pinnedTrackSid === focusTrack.participant.sid}
                      onToggleSettings={onToggleSettings}
                    />
                  </TrackRefContext.Provider>
                </div>
              </div>
              <ScreenShareWarning
                isScreenSharing={isLocalScreenSharing && focusTrack.source === Track.Source.ScreenShare}
                onStopPresenting={onStopScreenShare}
              />
            </>
          )}
        </div>

        {/* Participant tiles - collapsible on mobile, sidebar on desktop */}
        {sideTracks.length > 0 && (
          <div className="shrink-0 lg:flex lg:flex-col lg:gap-2 lg:overflow-y-auto animate-slide-right lg:w-64">
            {/* Mobile: Toggle button + collapsible participants */}
            {!hasScreenShare && (
              <div className="lg:hidden">
                <button
                  onClick={() => setMobileParticipantsExpanded(!mobileParticipantsExpanded)}
                  className="w-full flex items-center justify-between px-3 py-1.5 bg-[#303134] rounded-lg transition-all"
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    <span className="text-xs text-white font-medium">
                      {t("participant.participantCount", { count: sideTracks.length })}
                    </span>
                  </div>
                  {mobileParticipantsExpanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  )}
                </button>

                {/* Expanded: Compact inline participant list */}
                {mobileParticipantsExpanded && (
                  <div
                    className={
                      sideTracks.length > 4
                        ? "grid grid-cols-4 sm:grid-cols-5 gap-2 overflow-y-auto max-h-48 py-2 px-1 scrollbar-hide"
                        : "flex gap-3 overflow-x-auto py-2 px-1 scrollbar-hide"
                    }
                  >
                    {sideTracks.map((track) => {
                      const key = track.publication?.trackSid || `placeholder-${track.participant.sid}`;
                      const name = track.participant.name || track.participant.identity || t("participant.guest");
                      const initial = name.charAt(0).toUpperCase();
                      const isLocal = track.participant.sid === localParticipantSid;
                      const compactAvatarUrl = avatarUrlForParticipant(track);

                      return (
                        <div
                          key={key}
                          className="flex flex-col items-center gap-1 shrink-0"
                          onClick={() => handlePin(track.participant.sid)}
                        >
                          {/* Small avatar circle */}
                          <div className="relative">
                            <div className="w-10 h-10 rounded-full bg-linear-to-br from-gray-600 to-gray-700 flex items-center justify-center overflow-hidden text-white text-sm font-semibold shadow-md border border-white/10">
                              {compactAvatarUrl ? (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                  src={compactAvatarUrl}
                                  alt=""
                                  className="h-full w-full object-cover"
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                initial
                              )}
                            </div>
                            {/* Mic status indicator */}
                            <div
                              className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center border border-[#202124] shadow-sm ${
                                track.participant.isMicrophoneEnabled ? "bg-primary" : "bg-error"
                              }`}
                            >
                              {track.participant.isMicrophoneEnabled ? (
                                <Users className="hidden" /> // Hack to keep import if needed
                              ) : null}
                              {track.participant.isMicrophoneEnabled ? (
                                <Mic className="w-3 h-3 text-white" />
                              ) : (
                                <MicOff className="w-3 h-3 text-white" />
                              )}
                            </div>
                          </div>
                          {/* Name */}
                          <span className="text-[10px] text-gray-300 max-w-12 truncate text-center">
                            {isLocal ? t("participant.you") : name.split(" ")[0]}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Desktop: Always visible sidebar */}
            <div className="hidden lg:flex lg:flex-col gap-2">
              {sideTracks.map((track) => {
                const key = track.publication?.trackSid || `placeholder-${track.participant.sid}`;
                return (
                  <TrackRefContext.Provider key={key} value={track}>
                    <div className="shrink-0 h-32 lg:h-36">
                      <ParticipantTile
                        track={track}
                        isLocal={track.participant.sid === localParticipantSid}
                        avatarUrl={avatarUrlForParticipant(track)}
                        {...tileHandProps(track.participant.sid, track.participant.sid === localParticipantSid)}
                        {...tileModerationProps(track.participant.sid === localParticipantSid)}
                        onPin={() => handlePin(track.participant.sid)}
                        isPinned={pinnedTrackSid === track.participant.sid}
                        onToggleSettings={onToggleSettings}
                      />
                    </div>
                  </TrackRefContext.Provider>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Define max tiles based on device
  // Desktop: 9 tiles (3x3)
  // Mobile/Tablet: 8 tiles (4x2 or similar)
  const MAX_TILES = isMobileOrTablet ? 8 : 9;

  // Show "Others" if we have more participants than the grid can fit
  const showOthersTile = sortedCameraTracks.length > MAX_TILES;

  // If showing "Others", we need 1 slot for it, so we show (MAX - 1) videos
  // If NOT showing "Others", we show all participants (up to MAX)
  const maxVisibleVideoTracks = showOthersTile ? MAX_TILES - 1 : MAX_TILES;

  const visibleTracks = sortedCameraTracks.slice(0, maxVisibleVideoTracks);
  const othersCount = sortedCameraTracks.length - maxVisibleVideoTracks;
  const totalTiles = visibleTracks.length + (showOthersTile ? 1 : 0);

  const gridClass = GRID_CLASSES[totalTiles] || DEFAULT_GRID_CLASS;

  const containerClass =
    sortedCameraTracks.length === 1
      ? "h-full w-full"
      : `min-h-full lg:h-full grid gap-2 p-1 ${gridClass} animate-zoom-in`;

  return (
    <div className={containerClass} role="main" aria-label={t("participant.gridAria")}>
      {visibleTracks.map((track: TrackReferenceOrPlaceholder, index: number) => {
        /** Use participant sid as key since publication may not exist for camera-off participants */
        const key = track.publication?.trackSid || `placeholder-${track.participant.sid}`;
        // Apply special grid classes for 3-participant layout (2 on top, 1 centered below)
        const threeParticipantClass =
          !showOthersTile && sortedCameraTracks.length === 3 ? getThreeParticipantClass(index) : "";
        return (
          <TrackRefContext.Provider key={key} value={track}>
            <div className={`h-full ${threeParticipantClass}`}>
              <ParticipantTile
                track={track}
                isLocal={track.participant.sid === localParticipantSid}
                avatarUrl={avatarUrlForParticipant(track)}
                {...tileHandProps(track.participant.sid, track.participant.sid === localParticipantSid)}
                {...tileModerationProps(track.participant.sid === localParticipantSid)}
                onPin={() => handlePin(track.participant.sid)}
                isPinned={pinnedTrackSid === track.participant.sid}
                onToggleSettings={onToggleSettings}
              />
            </div>
          </TrackRefContext.Provider>
        );
      })}

      {/* "Others" Tile */}
      {showOthersTile && (
        <div
          className="col-span-1 h-full relative overflow-hidden rounded-lg bg-linear-to-br from-gray-800 to-gray-900 flex flex-col items-center justify-center cursor-pointer hover:brightness-110 transition-all duration-200 group border border-white/5"
          onClick={onToggleParticipants}
          role="button"
          aria-label={t("participant.showOthers", { count: othersCount })}
        >
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gray-600 ring-4 ring-gray-700 flex items-center justify-center mb-2 shadow-lg group-hover:scale-110 transition-transform duration-200">
            <span className="text-white text-xl sm:text-2xl font-semibold">
              {t("participant.othersCount", { count: othersCount })}
            </span>
          </div>
          <span className="text-gray-300 text-sm font-medium mt-2">{t("participant.others")}</span>
        </div>
      )}
    </div>
  );
});
